import { AlertCircle, CheckCircle2 } from 'lucide-react';
import type { FormLinkState, FormPharmacy } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { HEADING, cn } from '../../components/ui';

/** Every way a link can be short of the form, and the one we cannot find. */
export type ClosedState = Exclude<FormLinkState, 'ready' | 'verify_dob'> | 'missing';

const WORDS: Record<ClosedState, MessageKey> = {
  submitted: 'form.state.submitted',
  expired: 'form.state.expired',
  locked: 'form.state.locked',
  cancelled: 'form.state.cancelled',
  not_needed: 'form.state.not_needed',
  replaced: 'form.state.replaced',
  used: 'form.state.used',
  restart: 'form.state.restart',
  unavailable: 'form.state.unavailable',
  missing: 'form.state.missing',
};

/** Where a new link is the answer, say who gives one. */
const NEEDS_NEW_LINK: ReadonlySet<ClosedState> = new Set(['expired', 'locked', 'used']);

/**
 * Why the form is not here, in one sentence, and what to do next. Never more
 * than that: before the date of birth nothing about the form is shown, so a
 * forwarded link tells its holder nothing.
 */
export function FormStateMessage({
  state,
  pharmacy,
}: {
  state: ClosedState;
  pharmacy: FormPharmacy | null;
}) {
  const { t } = useI18n();
  const phone = pharmacy?.supportPhone ?? null;
  const done = state === 'submitted';
  const Icon = done ? CheckCircle2 : AlertCircle;
  return (
    <div className="space-y-4">
      <h1
        tabIndex={-1}
        ref={(node) => node?.focus()}
        className={cn('flex items-start gap-3', HEADING)}
      >
        <Icon
          className={done ? 'mt-1 h-6 w-6 shrink-0 text-success' : 'mt-1 h-6 w-6 shrink-0 text-danger'}
          aria-hidden="true"
        />
        {t(WORDS[state])}
      </h1>
      {!done && (
        <p className="text-lg text-foreground">
          {NEEDS_NEW_LINK.has(state)
            ? phone
              ? t('form.callForLink', { phone })
              : t('form.callPharmacy')
            : phone
              ? t('form.callPhone', { phone })
              : null}
        </p>
      )}
    </div>
  );
}
